import {
    Component,
    ViewChild,
    ViewContainerRef,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChange
} from '@angular/core';
import { AbstractPageBuilderService } from '../../services/abstract-page-builder.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { Subscription } from 'rxjs';
import { BlockModel } from '../../models/block.model';
import { AbstractFormFieldComponent } from '../FormField/abstract-form-field.component';
import { FormFieldModel } from '../../models/form-field.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

@Component({
    selector: 'agpr-block-form',
    templateUrl: './block-form.component.html',
    styleUrls: ['./block-form.component.scss'],
    standalone: false
})
export class BlockFormComponent implements OnInit, OnDestroy {
    @ViewChild('formFieldsContainer', { read: ViewContainerRef, static: true }) fieldsContainer: ViewContainerRef;

    private fieldComponents: AbstractFormFieldComponent[] = [];

    private subscriptions: Subscription[] = [];
    private block: BlockModel;

    readonly messageSeverityEnum = messageSeverityEnum;

    get hasNoFields(): boolean {
        return this.block?.type?.fields?.length === 0;
    }

    constructor(
        public pageBuilderService: AbstractPageBuilderService
    ) {
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.pageBuilderService.getSelectedBlock().subscribe(
                (block: BlockModel) => {
                    this.block = block;
                    this.loadFields();
                }
            ),
            this.pageBuilderService.getSelectedBlockUpdated().subscribe(
                () => {
                    this.loadFields();
                }
            )
        );
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
        this.fieldsContainer.clear();
    }

    public createField(field: FormFieldModel, id: string, container: ViewContainerRef = this.fieldsContainer): void {
        const componentRef = container.createComponent<AbstractFormFieldComponent>(field.fieldComponent);
        const fieldComponent = componentRef.instance;

        // Listen to changes first so that if the setter makes changes, we're listening
        fieldComponent.changes.subscribe((fieldValue) => {
            this.setPropertyValue(field.property, fieldValue, field?.config?.usePreviousValue);
        });

        const value = this.getPropertyValue(field.property);
        fieldComponent.field = field;
        fieldComponent.block = this.block;
        fieldComponent.value = value;
        fieldComponent.id = id;
        if ('ngOnChanges' in fieldComponent) {
            (<unknown>fieldComponent as OnChanges).ngOnChanges({
                id: new SimpleChange(undefined, id, true),
                field: new SimpleChange(undefined, field, true),
                block: new SimpleChange(undefined, this.block, true),
                value: new SimpleChange(undefined, value, true)
            });
        }
        this.fieldComponents.push(fieldComponent);
    }

    loadFields(): void {
        this.fieldsContainer.clear();
        this.fieldComponents = [];

        if (this.block?.type?.fields) {
            for (let i = 0; i < this.block.type.fields.length; i++) {
                this.createField(this.block.type.fields[i], 'field_' + i);
            }
        }
    }

    getPropertyValue(property: string): string {
        const parts = property.split('.');
        let prop: any = this.block.displayElement;
        for (let i = 0, l = parts.length; i < l; i++) {
            prop = prop[parts[i]];
        }

        return prop;
    }

    getFieldComponentByProperty(property: string): AbstractFormFieldComponent {
        const index = this.block.type.fields.findIndex(x => x.property === property);
        return this.fieldComponents.find(x => x.id === 'field_' + index);
    }

    async setPropertyValue(property: string, value: any, usePreviousValue?: boolean): Promise<void> {
        const parts = property.split('.');
        let obj: any = this.block?.displayElement;
        if (!obj) {
            return;
        }
        for (let i = 0, l = parts.length; i < l - 1; i++) {
            obj = obj[parts[i]];
            if (!obj) {
                return;
            }
        }
        const previousValue = usePreviousValue ? obj[parts[parts.length - 1]] : null;
        obj[parts[parts.length - 1]] = value;

        // Force changes on the block so it will re-render
        if (this.block?.displayElement?.styles) {
            this.block.displayElement.styles = this.block.displayElement.styles.clone();
        }

        this.pageBuilderService.notifyBlockDisplayElementChanged(this.block);

        if (await this.block.displayElement.onUpdate()) {
            for (const fieldComponent of this.fieldComponents) {
                const field = fieldComponent.field;
                const oldValue = fieldComponent.value;
                const newValue = this.getPropertyValue(field.property);

                fieldComponent.value = newValue;

                if ('ngOnChanges' in fieldComponent) {
                    (<unknown>fieldComponent as OnChanges).ngOnChanges({
                        value: new SimpleChange(oldValue, newValue, true),
                        [property]: new SimpleChange(previousValue, value, false)
                    });
                }
            }
        }

        this.pageBuilderService.blockFormUpdated(this.block, property, value);
    }
}
